/**
 * Per-scene draft persistence for the KMD editor (EDITOR-1 Phase 3, D3-A5).
 *
 * Owner requirement Q5: drafts persist per scene / document / project, NOT as
 * one global slot, and an accidental reload must not destroy authoring work —
 * including in a scene that has never been saved.
 *
 * That last part is the awkward one. A brand-new canvas has no id yet, so a
 * draft written before the first save has nowhere permanent to live. It goes
 * under a provisional key and is migrated the moment the scene acquires an
 * identity; on reload into an unsaved scene the panel OFFERS the provisional
 * draft rather than silently restoring it (restoring someone else's leftover
 * text into a blank canvas would be its own kind of data loss).
 *
 * Storage is local and is never synced; nothing in the UI may suggest it is.
 */

const PREFIX = "khiyarsa:kmd-draft:";
const PROVISIONAL = `${PREFIX}unsaved`;

export interface KmdDraft {
  text: string;
  /** caret offset, so a reload puts the cursor back */
  caret: number;
  /** was the diagram still driven by this text when the draft was written */
  bound: boolean;
  savedAt: number;
}

export interface DraftStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
  key(index: number): string | null;
  readonly length: number;
}

const keyFor = (sceneId: string | null): string =>
  sceneId ? `${PREFIX}${sceneId}` : PROVISIONAL;

const read = (store: DraftStore, key: string): KmdDraft | null => {
  try {
    const raw = store.getItem(key);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as Partial<KmdDraft>;
    if (typeof parsed.text !== "string") {
      return null;
    }
    return {
      text: parsed.text,
      caret: typeof parsed.caret === "number" ? parsed.caret : 0,
      bound: parsed.bound === true,
      savedAt: typeof parsed.savedAt === "number" ? parsed.savedAt : 0,
    };
  } catch {
    // a corrupt draft must never take the editor down with it
    return null;
  }
};

export const saveDraft = (
  store: DraftStore,
  sceneId: string | null,
  draft: Omit<KmdDraft, "savedAt">,
  now: number,
): void => {
  try {
    const record: KmdDraft = { ...draft, savedAt: now };
    store.setItem(keyFor(sceneId), JSON.stringify(record));
  } catch {
    // quota or private-mode failures are not worth interrupting authoring for
  }
};

export const loadDraft = (
  store: DraftStore,
  sceneId: string | null,
): KmdDraft | null => read(store, keyFor(sceneId));

export const clearDraft = (store: DraftStore, sceneId: string | null): void => {
  try {
    store.removeItem(keyFor(sceneId));
  } catch {
    /* ignore */
  }
};

/**
 * A draft written before the scene had an identity, offered for recovery.
 * Returned only when it has actual content — an empty provisional draft is
 * noise, not work worth recovering.
 */
export const pendingRecovery = (store: DraftStore): KmdDraft | null => {
  const draft = read(store, PROVISIONAL);
  return draft && draft.text.trim().length > 0 ? draft : null;
};

export const discardRecovery = (store: DraftStore): void => {
  try {
    store.removeItem(PROVISIONAL);
  } catch {
    /* ignore */
  }
};

/**
 * Move the provisional draft under the scene's real key once it has one.
 * Refuses to overwrite a draft the scene already has — the scene's own work
 * outranks whatever was typed before it existed.
 */
export const adoptProvisional = (
  store: DraftStore,
  sceneId: string,
): KmdDraft | null => {
  const draft = read(store, PROVISIONAL);
  if (!draft) {
    return null;
  }
  if (read(store, keyFor(sceneId))) {
    return null;
  }
  try {
    store.setItem(keyFor(sceneId), JSON.stringify(draft));
    store.removeItem(PROVISIONAL);
  } catch {
    return null;
  }
  return draft;
};

/** every scene id that currently holds a draft (diagnostics / cleanup) */
export const listDraftScenes = (store: DraftStore): string[] => {
  const out: string[] = [];
  for (let i = 0; i < store.length; i++) {
    const key = store.key(i);
    if (key && key.startsWith(PREFIX) && key !== PROVISIONAL) {
      out.push(key.slice(PREFIX.length));
    }
  }
  return out;
};
